import type { ImageScenario } from '../game/imageTypes';

/**
 * Level 4 -- a Jira project permission scheme.
 *
 * ============================ DRAFT CLASSIFICATION ==========================
 * Regions are measured by tools/find_arrows.py and should be correct. Which
 * arrows are real defects is a draft and needs confirming before release.
 *
 * This level is the most opinionated of the four, because several of these
 * grants are defensible depending on how open the organisation intends to be.
 * The draft treats "an app can do it but no human can" and "bypasses issue
 * security" as real, and broad-but-conventional read access as fine. That line
 * is a judgement call and worth confirming explicitly.
 * ============================================================================
 *
 * Image is 2000 x 1039. All regions are percentages of those dimensions.
 */
export const permissions: ImageScenario = {
  id: 'jira-permissions',
  title: 'Who can actually do what',
  subtitle: 'Project permission scheme',
  brief:
    'A project permission scheme, showing which roles hold which rights. Seven arrows point at individual grants. Broad access is not automatically wrong — plenty of organisations are deliberately open internally. Look instead for grants that put a capability somewhere nobody intended.',

  source: {
    toolId: 'jira',
    screen: 'Project settings › Permissions',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/permissions.webp',
    width: 2000,
    height: 1039,
    alt: 'Jira permission scheme listing Administration Permissions and Project Permissions, with grants to Space Role (Administrators), Space Role (atlassian-addons-project-access) and Application access (Any logged in user).',
  },

  arrows: [
    {
      id: 'administer-administrators',
      label: '1',
      region: { x: 80.0, y: 9.05, w: 3.9, h: 5.0 },
      description:
        'Arrow pointing at the Administrators role holding Administer Projects',
      decoyNote:
        'Administrators holding Administer Projects is the single most expected line on this entire screen. If anything, this is the grant you would check exists.',
    },
    {
      id: 'administer-addons',
      label: '2',
      region: { x: 91.95, y: 15.11, w: 5.7, h: 6.16 },
      description:
        'Arrow pointing at the atlassian-addons-project-access role in the Administration Permissions block',
      decoyNote:
        'atlassian-addons-project-access is a role Atlassian creates and maintains so installed apps can operate inside a project. Seeing it against the administration permissions looks alarming, but it is the stock configuration — removing it generally breaks the apps rather than tightening anything. The add-ons role only becomes a finding where it holds a capability alone, with no human alongside it.',
    },
    {
      id: 'edit-workflows-addons',
      label: '3',
      region: { x: 91.7, y: 28.59, w: 4.95, h: 7.22 },
      description:
        'Arrow pointing at Edit Workflows, granted only to the atlassian-addons-project-access role',
    },
    {
      id: 'browse-any-user',
      label: '4',
      region: { x: 88.27, y: 51.68, w: 3.0, h: 5.39 },
      description:
        'Arrow pointing at Browse Projects granted to Application access (Any logged in user)',
      decoyNote:
        'Browse Projects open to every licensed user is a deliberate stance, and a common one: most engineering organisations want internal work visible by default and reserve restriction for genuinely sensitive projects. It is only a finding if this particular project was meant to be closed — which nothing on this screen suggests.',
    },
    {
      id: 'manage-versions-addons',
      label: '5',
      region: { x: 76.0, y: 67.95, w: 6.55, h: 5.0 },
      description:
        'Arrow pointing at Manage Versions (Releases), granted only to the atlassian-addons-project-access role',
    },
    {
      id: 'aggregated-data-any-user',
      label: '6',
      region: { x: 89.55, y: 82.1, w: 4.25, h: 5.29 },
      description:
        'Arrow pointing at View aggregated data granted to Application access (Any logged in user)',
    },
    {
      id: 'dev-tools-any-user',
      label: '7',
      region: { x: 76.75, y: 93.36, w: 6.3, h: 5.0 },
      description:
        'Arrow pointing at View Development Tools granted to Application access (Any logged in user)',
      decoyNote:
        'This exposes branch, commit and pull request information on issues to every licensed user. That is the normal default, and it is the same population that can already browse the project — engineers seeing each other’s commits is usually the point rather than the risk.',
    },
  ],

  defects: [
    {
      id: 'd-edit-workflows-addons-only',
      arrowId: 'edit-workflows-addons',
      title: 'Only an app can edit workflows — no human can',
      severity: 'major',
      explanation:
        'Edit Workflows is assigned to exactly one holder, and it is the add-ons role. Not Administrators, not project leads: nobody who can be asked a question. That cuts both ways. Any installed app may rewrite the workflow unattended, while the people accountable for the process cannot change it without first editing the permission scheme — so the change that does eventually happen is made in a hurry by someone who has just widened their own access.',
      fix: 'Grant Edit Workflows to Administrators as well, so a human owns it. Leave the add-ons role only if an app you actually rely on needs it, and check which one before assuming it does.',
    },
    {
      id: 'd-manage-versions-addons-only',
      arrowId: 'manage-versions-addons',
      title: 'Releases can be managed only by the app role',
      severity: 'major',
      explanation:
        'Manage Versions covers creating, editing, merging, releasing and deleting versions, and it is granted solely to the add-ons role. No release manager can cut a release through the UI, while an app holds the right to delete a version outright — an action that silently detaches every issue attributed to it and quietly rewrites release history.',
      fix: 'Give Manage Versions to whoever actually runs releases. Treat the destructive half as the reason to care: deleting a version is not recoverable by undo.',
    },
    {
      id: 'd-aggregated-data-bypass',
      arrowId: 'aggregated-data-any-user',
      title: 'Aggregated data bypasses issue-level permissions',
      severity: 'major',
      explanation:
        'This is the one broad grant that is genuinely different from the others. As the description on the row states, it shows combined and summarised project data regardless of a user’s individual permissions — so it deliberately steps around issue security. Every other "any logged in user" grant on this screen still respects what an individual may see; this one does not, which makes it the wrong permission to hand to everybody by default.',
      fix: 'Limit View aggregated data to the roles that need reporting across the project. Grant Browse Projects broadly if you want openness — it stays inside each user’s own permissions.',
    },
  ],
};
